"use client";

import React, { useState, useEffect, useRef, useContext } from "react";
import PropTypes from "prop-types";
import styled, { ThemeProvider } from "styled-components";
import { AppearanceContext } from "./AppearanceContext.js";
import { getAllModeStyles } from "./modes";
import { jsx as _jsx } from "react/jsx-runtime";
const AppearanceRoot = styled.div.withConfig({
  componentId: "sc-1gjs9k6-0"
})(["", " background:var(--unity-background-color-level-current,#ffffff);color:var(--unity-text-color-primary,#212225);"], _ref => {
  let {
    $mode,
    $parentMode,
    $forceOutput
  } = _ref;
  /* If the parent is the same as the current mode, don't output any styles */
  if ($mode === $parentMode && !$forceOutput) return;
  const lightStyles = getAllModeStyles("light");
  const darkStyles = getAllModeStyles("dark");
  /* Contrary to Appearance we need to output the light styles to override anything set by a parent */
  if ($mode === "light") {
    return lightStyles;
  } else if ($mode === "dark") {
    return darkStyles;
  } else {
    return `
        ${lightStyles}
        @media(prefers-color-scheme: dark) { ${darkStyles} }
      `;
  }
});

/** Provides "Appearance" support to a section of a page. Usually to provide a dark/inverted section within a normally "light" page. This can be used with or without using <Appearance> around the full application. */
const AppearanceSection = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    mode = "os",
    renderAs,
    ...props
  } = _ref2;
  const appearanceContext = useContext(AppearanceContext);
  /**
   * Store the context object in state (see https://reactjs.org/docs/context.html#caveats)
   * Using an object now lets us add additional properties in the future.
   */
  const [contextValue, setContextValue] = useState({
    mode
  });
  const firstRender = useRef(true);
  // Update the contextValue state everytime the `mode` prop changes
  useEffect(() => {
    /**
     * Don't need the effect to set the state on the first render since it
     * already has a default value.
     */
    if (firstRender.current === true) {
      firstRender.current = false;
    } else {
      setContextValue({
        mode
      });
    }
  }, [mode]);
  return /*#__PURE__*/_jsx(AppearanceContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(ThemeProvider, {
      theme: {
        edenAppearanceMode: contextValue.mode
      },
      children: /*#__PURE__*/_jsx(AppearanceRoot, {
        $mode: mode,
        $parentMode: appearanceContext === null || appearanceContext === void 0 ? void 0 : appearanceContext.mode,
        $forceOutput: props.forceOutput,
        as: renderAs,
        ...props,
        ref: ref,
        children: children
      })
    })
  });
});
AppearanceSection.displayName = "AppearanceSection";
AppearanceSection.propTypes = {
  /** What mode should be used */
  mode: PropTypes.oneOf(["light", "dark", "os"]),
  /** Content */
  children: PropTypes.node,
  /** Normally this won't output CSS if the current section is inside a parent section of the same `mode`. In specific circumstances, you may need to force it to. */
  forceOutput: PropTypes.bool,
  /** The HTML tag string (e.g., `"div"`) or React component to render as. */
  renderAs: PropTypes.elementType
};
export default AppearanceSection;